import { Alert, Checkbox, Select, Space, Typography } from 'antd';
import type { ScreenDiagnostic, ScreenVocabulary } from '@/api/screens';
import { errorAt } from './diagnostics';
import { LocalizedField } from './LocalizedField';
import { PAGE_COPY, VARIANT_COPY, type JourneyPages, type LocalizedText, type PageDef, type PageName } from './model';

const { Text } = Typography;

/**
 * The welcome, terms and final pages (OL-23). The variants are the server's list and what is legal is the
 * gate's answer, on the field concerned. Consent wording is deliberately not here: the T&C acceptance is the
 * journey's first declaration, whose text the consent record hashes.
 */
export function PageSettings({
  pages,
  vocabulary,
  locales,
  diagnostics,
  onChange,
}: {
  pages: JourneyPages | undefined;
  vocabulary: ScreenVocabulary;
  locales: string[];
  diagnostics: ScreenDiagnostic[];
  onChange: (next: JourneyPages | undefined) => void;
}) {
  const setPage = (name: PageName, page: PageDef | undefined) => {
    const next = { ...(pages ?? {}) };
    if (page) next[name] = page;
    else delete next[name];
    onChange(Object.keys(next).length > 0 ? next : undefined);
  };
  return (
    <Space direction="vertical" size={14} style={{ width: '100%' }} data-testid="page-settings">
      {(['welcome', 'terms', 'final'] as const).map((name) => {
        const page = pages?.[name];
        const base = `/journey/pages/${name}`;
        const update = (updates: Partial<PageDef>) => {
          const next = { ...page, ...updates } as PageDef;
          for (const [k, v] of Object.entries(next)) {
            if (v === undefined) delete (next as unknown as Record<string, unknown>)[k];
          }
          setPage(name, next);
        };
        const text = (label: string, hint: string, field: keyof PageDef, multiline = false) => (
          <LocalizedField
            label={label}
            hint={hint}
            multiline={multiline}
            value={page?.[field] as LocalizedText | undefined}
            locales={locales}
            diagnostics={diagnostics}
            path={`${base}/${field}`}
            onChange={(value) => update({ [field]: value })}
          />
        );
        const variants = vocabulary.pageVariants?.[name] ?? [];
        return (
          <div key={name} data-testid={`page-${name}`}>
            <Checkbox
              data-testid={`page-${name}-on`}
              checked={!!page}
              onChange={(e) => setPage(name, e.target.checked ? { variant: variants[0] } : undefined)}
            >
              {PAGE_COPY[name].label}
            </Checkbox>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {PAGE_COPY[name].hint}
              </Text>
            </div>
            {page && (
              <Space direction="vertical" size={10} style={{ width: '100%', marginTop: 8 }}>
                <div>
                  <div style={{ fontSize: 12, marginBottom: 4 }}>Variant</div>
                  <Select
                    style={{ width: 320 }}
                    aria-label={`${PAGE_COPY[name].label} variant`}
                    data-testid={`page-${name}-variant`}
                    value={page.variant}
                    status={errorAt(diagnostics, `${base}/variant`) ? 'error' : undefined}
                    options={variants.map((v) => ({ value: v, label: VARIANT_COPY[v] ?? v }))}
                    onChange={(variant: string) => {
                      // A field the new variant does not have would only come back as a refusal.
                      const { continueLabel: _c, nextSteps: _n, ...rest } = page;
                      setPage(name, {
                        ...rest,
                        variant,
                        ...(name === 'terms' && variant === 'NOTICE' && _c ? { continueLabel: _c } : {}),
                        ...(variant === 'NEXT_STEPS' ? { nextSteps: _n ?? [{ en: '' }] } : {}),
                      });
                    }}
                  />
                </div>
                {name === 'terms' && page.variant === 'CONSENT' && (
                  <Alert
                    type="info"
                    showIcon
                    data-testid="page-terms-consent-note"
                    message="The acceptance is your first step"
                    description="This page shows the journey's first step, which must be a required declaration, with one step at a time switched on. Its text, version and consent record are the declaration's own: write the words there, in each language."
                  />
                )}
                {name === 'terms' && page.variant === 'NOTICE' && (
                  <Alert
                    type="warning"
                    showIcon
                    data-testid="page-terms-notice-note"
                    message="A notice is not consent"
                    description="It records nothing. If the applicant's acceptance matters, use the consent variant."
                  />
                )}
                {text('Heading', "Empty uses the room's own heading.", 'heading')}
                {text(
                  'Body',
                  name === 'terms' && page.variant === 'CONSENT'
                    ? 'An optional line above the declaration. It is not part of the consent record.'
                    : "Plain text. Line breaks are kept. Empty uses the room's own words.",
                  'body',
                  true,
                )}
                {name === 'welcome' && text('Start button', "Empty uses the room's own label.", 'startLabel')}
                {name === 'terms' &&
                  page.variant === 'NOTICE' &&
                  text('Continue button', "Empty uses the room's own label.", 'continueLabel')}
                {name === 'final' &&
                  page.variant === 'NEXT_STEPS' &&
                  (page.nextSteps ?? []).map((step, i) => (
                    <LocalizedField
                      key={i}
                      label={`Next step ${i + 1}`}
                      value={step}
                      locales={locales}
                      diagnostics={diagnostics}
                      path={`${base}/nextSteps/${i}`}
                      onChange={(value) => {
                        const steps = [...(page.nextSteps ?? [])];
                        if (value) steps[i] = value;
                        else steps.splice(i, 1);
                        update({ nextSteps: steps });
                      }}
                    />
                  ))}
                {name === 'final' &&
                  page.variant === 'NEXT_STEPS' &&
                  (page.nextSteps?.length ?? 0) < (vocabulary.limits.maxNextSteps ?? 6) && (
                    <a
                      role="button"
                      data-testid="page-final-add-step"
                      onClick={() => update({ nextSteps: [...(page.nextSteps ?? []), { en: '' }] })}
                    >
                      Add a next step
                    </a>
                  )}
              </Space>
            )}
          </div>
        );
      })}
    </Space>
  );
}
